import { useState, useEffect } from 'react';
import { toast } from 'react-toastify';

export const useProjectState = (socket, showNotification) => {
  const [projects, setProjects] = useState([]);
  const [selectedProject, setSelectedProject] = useState(null);
  const [myTasks, setMyTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMyTasks, setLoadingMyTasks] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [loadingTemplates, setLoadingTemplates] = useState(false);

  const apiCall = async (url, options = {}) => {
    const token = localStorage.getItem('token');
    const response = await fetch(`/api/project-tracker${url}`, {
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
        ...options.headers,
      },
      ...options,
    });
    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw new Error(error.error || 'API request failed');
    }
    return response.json();
  };

  const loadProjects = async () => {
    try {
      setLoading(true);
      const data = await apiCall('/projects');
      setProjects(data);
    } catch (error) {
      showNotification('Failed to load projects: ' + error.message, 'error');
    } finally {
      setLoading(false);
    }
  };

  const loadProject = async (projectId) => {
    try {
      const project = await apiCall(`/projects/${projectId}`);
      setSelectedProject(project);
    } catch (error) {
      showNotification('Failed to load project: ' + error.message, 'error');
    }
  };

  const loadMyTasks = async () => {
    try {
      setLoadingMyTasks(true);
      const tasks = await apiCall('/my-tasks');
      setMyTasks(tasks);
    } catch (error) {
      showNotification('Failed to load your tasks: ' + error.message, 'error');
    } finally {
      setLoadingMyTasks(false);
    }
  };

  const completeMyTask = async (taskId) => {
    try {
      const updatedTask = await apiCall(`/tasks/${taskId}`, {
        method: 'PUT',
        body: JSON.stringify({ is_completed: true }),
      });

      // Remove completed task from myTasks list
      setMyTasks((prev) => prev.filter((task) => task.id !== taskId));

      // Update selected project if the task is in the currently selected project
      if (selectedProject) {
        setSelectedProject((prev) => ({
          ...prev,
          tasks: (prev.tasks || []).map((t) => (t.id === taskId ? updatedTask : t)),
        }));
      }

      // Emit socket event
      if (socket) {
        socket.emit('project-tracker-update', {
          type: 'task',
          action: 'update',
          data: updatedTask,
        });
      }

      showNotification('Task completed successfully!', 'success');
      return updatedTask;
    } catch (error) {
      showNotification('Failed to complete task: ' + error.message, 'error');
      throw error;
    }
  };

  const createProject = async (data) => {
    setIsSubmitting(true);
    try {
      const newProject = await apiCall('/projects', {
        method: 'POST',
        body: JSON.stringify(data),
      });
      // Don't update local state here - let the socket event handle it
      // This prevents duplicate projects when the socket event is received
      if (socket) {
        socket.emit('project-tracker-update', {
          type: 'project',
          action: 'create',
          data: newProject,
        });
      }
      showNotification('Project created successfully!', 'success');
      return newProject;
    } catch (error) {
      showNotification('Failed to create project: ' + error.message, 'error');
      throw error;
    } finally {
      setIsSubmitting(false);
    }
  };

  const updateProject = async (projectId, updates, isAutoStatusUpdate = false, updatedTasks = null) => {
    try {
      const updatedProject = await apiCall(`/projects/${projectId}`, {
        method: 'PUT',
        body: JSON.stringify(updates),
      });
      setProjects((prev) =>
        prev.map((p) => (p.id === projectId ? updatedProject : p))
      );
      if (selectedProject?.id === projectId) {
        if (isAutoStatusUpdate) {
          // For auto-status updates, use provided tasks array or fallback to current selectedProject
          setSelectedProject((prev) => ({
            ...prev,
            ...updatedProject,
            tasks: updatedTasks || selectedProject.tasks || [],
            notes: selectedProject.notes || []
          }));
        } else {
          // For manual updates, replace with the full response
          setSelectedProject(updatedProject);
        }
      }
      if (socket) {
        socket.emit('project-tracker-update', {
          type: 'project',
          action: 'update',
          data: updatedProject,
        });
      }
      return updatedProject;
    } catch (error) {
      showNotification('Failed to update project: ' + error.message, 'error');
      throw error;
    }
  };

  const createTask = async (data) => {
    setIsSubmitting(true);
    try {
      const newTask = await apiCall('/tasks', {
        method: 'POST',
        body: JSON.stringify(data),
      });
      // Don't update local state here - let the socket event handle it
      // This prevents duplicate tasks when the socket event is received
      if (socket) {
        socket.emit('project-tracker-update', {
          type: 'task',
          action: 'create',
          data: newTask,
        });
      }
      showNotification('Task created successfully!', 'success');
      return newTask;
    } catch (error) {
      showNotification('Failed to create task: ' + error.message, 'error');
      throw error;
    } finally {
      setIsSubmitting(false);
    }
  };

  const updateTask = async (taskId, updates) => {
    try {
      const updatedTask = await apiCall(`/tasks/${taskId}`, {
        method: 'PUT',
        body: JSON.stringify(updates),
      });
      if (selectedProject) {
        // Create the updated tasks array with the completed task
        const updatedTasks = (selectedProject.tasks || []).map((t) => (t.id === taskId ? updatedTask : t));
        
        setSelectedProject((prev) => ({
          ...prev,
          tasks: updatedTasks,
        }));
        
        // Auto-update project status when a task is completed
        if (updates.is_completed === true && selectedProject.status === 'not_started') {
          try {
            await updateProject(selectedProject.id, { 
              name: selectedProject.name,
              description: selectedProject.description || '',
              status: 'in_progress' 
            }, true, updatedTasks); // Pass the updated tasks array
          } catch (error) {
            console.error('Failed to auto-update project status:', error);
          }
        }
      }
      if (socket) {
        socket.emit('project-tracker-update', {
          type: 'task',
          action: 'update',
          data: updatedTask,
        });
      }
      return updatedTask;
    } catch (error) {
      showNotification('Failed to update task: ' + error.message, 'error');
      throw error;
    }
  };

  const deleteTask = async (taskId) => {
    try {
      await apiCall(`/tasks/${taskId}`, {
        method: 'DELETE',
      });
      if (selectedProject) {
        setSelectedProject((prev) => ({
          ...prev,
          tasks: prev.tasks.filter((t) => t.id !== taskId),
        }));
      }
      if (socket) {
        socket.emit('project-tracker-update', {
          type: 'task',
          action: 'delete',
          taskId,
        });
      }
      showNotification('Task deleted successfully!', 'success');
    } catch (error) {
      showNotification('Failed to delete task: ' + error.message, 'error');
      throw error;
    }
  };

  const deleteProject = async (projectId) => {
    try {
      await apiCall(`/projects/${projectId}`, {
        method: 'DELETE',
      });
      setProjects((prev) => prev.filter((p) => p.id !== projectId));
      if (selectedProject?.id === projectId) {
        setSelectedProject(null);
      }
      showNotification('Project deleted successfully!', 'success');
    } catch (error) {
      showNotification('Failed to delete project: ' + error.message, 'error');
      throw error;
    }
  };

  const loadTemplates = async () => {
    try {
      setLoadingTemplates(true);
      const data = await apiCall('/templates');
      setTemplates(data);
    } catch (error) {
      showNotification('Failed to load templates: ' + error.message, 'error');
    } finally {
      setLoadingTemplates(false);
    }
  };

  const saveAsTemplate = async (projectId, data) => {
    try {
      const template = await apiCall(`/projects/${projectId}/save-as-template`, {
        method: 'POST',
        body: JSON.stringify(data),
      });
      setTemplates((prev) => [...prev, template].sort((a, b) => a.name.localeCompare(b.name)));
      showNotification('Project saved as template!', 'success');
      return template;
    } catch (error) {
      showNotification('Failed to save template: ' + error.message, 'error');
      throw error;
    }
  };

  const createFromTemplate = async (templateId, data) => {
    setIsSubmitting(true);
    try {
      const newProject = await apiCall(`/templates/${templateId}/create-project`, {
        method: 'POST',
        body: JSON.stringify(data),
      });
      if (socket) {
        socket.emit('project-tracker-update', {
          type: 'project',
          action: 'create',
          data: newProject,
        });
      }
      showNotification('Project created from template!', 'success');
      return newProject;
    } catch (error) {
      showNotification('Failed to create from template: ' + error.message, 'error');
      throw error;
    } finally {
      setIsSubmitting(false);
    }
  };

  const deleteTemplate = async (templateId) => {
    try {
      await apiCall(`/templates/${templateId}`, { method: 'DELETE' });
      setTemplates((prev) => prev.filter((t) => t.id !== templateId));
      showNotification('Template deleted', 'success');
    } catch (error) {
      showNotification('Failed to delete template: ' + error.message, 'error');
      throw error;
    }
  };

  const addNote = async (projectId, noteText) => {
    try {
      await apiCall(`/projects/${projectId}/notes`, {
        method: 'POST',
        body: JSON.stringify({ content: noteText }),
      });
      // Don't update local state here - let the socket event handle it
      // This prevents duplicate notes when the socket event is received
      showNotification('Note added successfully!', 'success');
    } catch (error) {
      showNotification('Failed to add note: ' + error.message, 'error');
      throw error;
    }
  };

  const handleReorderTasks = async (activeTaskData, overTaskData) => {
    if (!selectedProject || activeTaskData.id === overTaskData.id) return;

    try {
      let newOrder;
      if (overTaskData.parent_task_id === null) {
        newOrder = overTaskData.order_index;
      } else {
        const parentTask = selectedProject.tasks.find(t => t.id === overTaskData.parent_task_id);
        const subtasks = selectedProject.tasks
          .filter(t => t.parent_task_id === overTaskData.parent_task_id)
          .sort((a, b) => a.order_index - b.order_index);
        const overIndex = subtasks.findIndex(t => t.id === overTaskData.id);
        newOrder = overIndex > 0 ? subtasks[overIndex - 1].order_index + 1 : 1;
      }

      await apiCall(`/tasks/${activeTaskData.id}/reorder`, {
        method: 'PUT',
        body: JSON.stringify({
          new_order: newOrder,
          new_parent_id: overTaskData.parent_task_id,
        }),
      });

      await loadProject(selectedProject.id);
      showNotification('Task reordered successfully!', 'success');
    } catch (error) {
      showNotification('Failed to reorder task: ' + error.message, 'error');
    }
  };

  const handlePromoteSubtask = async (task) => {
    try {
      await updateTask(task.id, {
        parent_task_id: null,
      });
      showNotification('Subtask promoted successfully!', 'success');
    } catch (error) {
      showNotification('Failed to promote subtask: ' + error.message, 'error');
    }
  };

  const handleConvertToSubtask = async (task, newParentId) => {
    try {
      await updateTask(task.id, {
        parent_task_id: newParentId,
      });
      showNotification('Task converted to subtask successfully!', 'success');
    } catch (error) {
      showNotification('Failed to convert task: ' + error.message, 'error');
    }
  };

  useEffect(() => {
    if (!socket) return;

    const handleProjectTrackerUpdate = (data) => {
      const { type, action, data: payload, projectId, taskId, newStatus } = data;
      
      switch (type) {
        case 'project': {
          if (action === 'create') {
            setProjects((prev) => [payload, ...prev]);
          } else if (action === 'update' || action === 'status_change' || action === 'status-change-auto' || action === 'priority_change') {
            setProjects((prev) =>
              prev.map((p) => (p.id === payload.id ? payload : p))
            );
            
            // Show notification for automatic status changes
            if (action === 'status-change-auto' && data.previousStatus && data.newStatus) {
              const statusMap = {
                'not_started': 'Not Started',
                'in_progress': 'In Progress', 
                'on_hold': 'On Hold',
                'completed': 'Completed',
                'cancelled': 'Cancelled'
              };
              const prevStatus = statusMap[data.previousStatus] || data.previousStatus;
              const newStatus = statusMap[data.newStatus] || data.newStatus;
              showNotification(`Project automatically updated: ${prevStatus} → ${newStatus}`, 'success');
            }
            
            // Show notification for priority changes
            if (action === 'priority_change' && data.newPriority) {
              showNotification(`Project priority updated to ${data.newPriority}`, 'success');
            }
            
            // Use callback form to get current selectedProject
            setSelectedProject((currentSelected) => {
              if (currentSelected?.id === payload.id) {
                return payload;
              }
              return currentSelected;
            });
          } else if (action === 'delete') {
            setProjects((prev) => prev.filter((p) => p.id !== projectId));
            setSelectedProject((currentSelected) => {
              if (currentSelected?.id === projectId) {
                return null;
              }
              return currentSelected;
            });
          }
          break;
        }
        case 'task': {
          if (action === 'create') {
            setSelectedProject((prev) => {
              if (prev && prev.id === payload.project_id) {
                return {
                  ...prev,
                  tasks: [payload, ...(prev.tasks || [])],
                };
              }
              return prev;
            });

            // Add to myTasks if assigned to current user
            const currentUserId = JSON.parse(localStorage.getItem('user') || '{}').id;
            if (payload.assigned_to === currentUserId && !payload.is_completed) {
              setMyTasks((prev) => [payload, ...prev]);
            }
          } else if (action === 'update' || action === 'status_change') {
            setSelectedProject((prev) => {
              if (prev && prev.tasks) {
                return {
                  ...prev,
                  tasks: (prev.tasks || []).map((t) => (t.id === payload.id ? payload : t)),
                };
              }
              return prev;
            });

            // Update myTasks accordingly
            const currentUserId = JSON.parse(localStorage.getItem('user') || '{}').id;
            if (payload.assigned_to === currentUserId && !payload.is_completed) {
              // Task is assigned to current user and not completed - add/update in myTasks
              setMyTasks((prev) => {
                const existingIndex = prev.findIndex(task => task.id === payload.id);
                if (existingIndex >= 0) {
                  // Update existing task
                  return prev.map((t) => (t.id === payload.id ? payload : t));
                } else {
                  // Add new task
                  return [payload, ...prev];
                }
              });
            } else {
              // Task is not assigned to current user or is completed - remove from myTasks
              setMyTasks((prev) => prev.filter((t) => t.id !== payload.id));
            }
          } else if (action === 'delete') {
            setSelectedProject((prev) => {
              if (prev && prev.tasks) {
                return {
                  ...prev,
                  tasks: (prev.tasks || []).filter((t) => t.id !== taskId),
                };
              }
              return prev;
            });

            // Remove from myTasks
            setMyTasks((prev) => prev.filter((t) => t.id !== taskId));
          }
          break;
        }
        case 'note': {
          if (action === 'create') {
            setSelectedProject((prev) => {
              if (prev && prev.id === payload.project_id) {
                return {
                  ...prev,
                  notes: [payload, ...(prev.notes || [])],
                };
              }
              return prev;
            });
          }
          break;
        }
      }
    };

    socket.on('project-tracker-update', handleProjectTrackerUpdate);
    return () => socket.off('project-tracker-update', handleProjectTrackerUpdate);
  }, [socket]);

  return {
    projects,
    setProjects,
    selectedProject,
    setSelectedProject,
    myTasks,
    setMyTasks,
    loading,
    setLoading,
    loadingMyTasks,
    setLoadingMyTasks,
    isSubmitting,
    setIsSubmitting,
    loadProjects,
    loadProject,
    loadMyTasks,
    createProject,
    updateProject,
    createTask,
    updateTask,
    deleteTask,
    deleteProject,
    completeMyTask,
    addNote,
    handleReorderTasks,
    handlePromoteSubtask,
    handleConvertToSubtask,
    templates,
    loadingTemplates,
    loadTemplates,
    saveAsTemplate,
    createFromTemplate,
    deleteTemplate,
  };
};